/* eslint-disable @typescript-eslint/naming-convention */
import { AxiosResponse, AxiosError } from "axios";
import request from "#/utils/request";
import InternalResponse from "#/types/internalResponse";
import { authHeaders } from "#/modules/auth/idp";

const toErrorResponse = (error: AxiosError): InternalResponse => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = error.response?.data as any;
  return {
    statusCode: error.response?.status ?? error.code,
    msg: data?.error?.message ?? data?.msg ?? error.message,
    success: false,
  };
};

export type PolicyType = {
  service_token_ids: string[];
  policy_id: string;
  policy_name: string;
  policy_type: string;
  current_version_id: string;
  policy_status: string;
  risk_level: number;
  created_at: string;
  updated_at: string;
  description: string;
};

export type PolicyDetailType = PolicyType & {
  rules_list: string[];
};

export type PolicyRequestType = {
  params?: {
    policy_status?: string;
  };
  token: string;
};

export default function getPolicies({ params, token }: PolicyRequestType): Promise<InternalResponse> {
  return request()({
    url: "/xecguard/v1/policies",
    method: "GET",
    params: params && Object.keys(params).length > 0 ? params : undefined,
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse<PolicyType[]>): InternalResponse<PolicyType[]> => ({
      statusCode: result.status,
      data: result.data,
      success: true,
    }),
  ).catch(toErrorResponse);
}

export type PatchPolicyParams = {
  policyId: string;
  token: string;
  data: {
    service_token_ids?: string[];
    rules_list?: string[];
    policy_name?: string;
    policy_type?: string;
    policy_status?: string;
    description?: string;
    risk_level?: number;
  };
};

export function patchPolicy({ policyId, token, data }: PatchPolicyParams): Promise<InternalResponse<PolicyDetailType>> {
  return request()({
    url: `/xecguard/v1/policies/${policyId}`,
    method: "PATCH",
    data,
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse<PolicyDetailType>): InternalResponse<PolicyDetailType> => ({
      statusCode: result.status,
      data: result.data,
      success: true,
    }),
  ).catch(toErrorResponse);
}

export type PostPolicyParams = {
  token: string;
  data: {
    service_token_ids: string[];
    policy_name: string;
    description?: string;
    risk_level: number;
    policy_type?: string;
    rules_list?: string[];
    policy_status?: string;
  };
};

export function postPolicy({ token, data }: PostPolicyParams): Promise<InternalResponse<PolicyDetailType>> {
  return request()({
    url: "/xecguard/v1/policies",
    method: "POST",
    data,
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse<PolicyDetailType>): InternalResponse<PolicyDetailType> => ({
      statusCode: result.status,
      data: result.data,
      success: true,
    }),
  ).catch(toErrorResponse);
}

export type InternalPolicySummary = {
  request_count: number;
  request_unsafe_count: number;
};

export type InternalPolicyDaywiseData = {
  date: string;
  request_count: number;
  request_unsafe_count: number;
};

export type InternalPolicyTokenwiseData = {
  service_token_name: string;
  division: string;
  request_unsafe_count: number;
};

export type InternalPolicyItem = {
  policy_name: string;
  policy_id: string;
  current_version_id: string;
  created_at: string;
  updated_at: string;
  description: string;
  risk_level: number;
  policy_status?: string;
  summary: InternalPolicySummary;
  daywise_data: InternalPolicyDaywiseData[];
  tokenwise_data: InternalPolicyTokenwiseData[];
};

export function getInternalPolicies({
  period,
  timezone,
  token,
}: {
  period: number;
  timezone?: string;
  token: string;
}): Promise<InternalResponse<InternalPolicyItem[]>> {
  return request()({
    url: "/xecguard/v1/internal/policies",
    method: "GET",
    params: {
      period,
      ...(timezone ? { timezone } : {}),
    },
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse<InternalPolicyItem[]>): InternalResponse<InternalPolicyItem[]> => ({
      statusCode: result.status,
      data: result.data,
      success: true,
    }),
  ).catch(toErrorResponse);
}

export function deletePolicyApi({ policyId, token }: { policyId: string; token: string }): Promise<InternalResponse> {
  return request()({
    url: `/xecguard/v1/policies/${policyId}`,
    method: "DELETE",
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse): InternalResponse => ({
      statusCode: result.status,
      success: true,
    }),
  ).catch(toErrorResponse);
}

export function getPolicyDetail(
  { policyId, token }: { policyId: string; token: string },
): Promise<InternalResponse<PolicyDetailType>> {
  return request()({
    url: `/xecguard/v1/policies/${policyId}`,
    method: "GET",
    headers: authHeaders(token),
  }).then(
    (result: AxiosResponse<PolicyDetailType>): InternalResponse<PolicyDetailType> => ({
      statusCode: result.status,
      data: result.data,
      success: true,
    }),
  ).catch(toErrorResponse);
}
